import React, { useCallback, useMemo } from "react";
import { useDocumentCategories } from "@api/documentCategory.api";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { matchesDocumentCategorySearch } from "@components/settings/DocumentCategories/utils";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import {
    applyCategoryToggle,
    collectItemValues,
} from "@components/pageHeader/filters/FilterPickerList/filterPickerTree";
import { DocumentCategoryIcon } from "@components/documents/DocumentCategoryIcon";
import { BaseFilterProps } from "../../lib";
import css from "./DocumentCategoryFilter.module.scss";

export const DocumentCategoryFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { data: documentCategoriesData } = useDocumentCategories();
    const documentCategories = useMemo(
        () => documentCategoriesData?.categories ?? [],
        [documentCategoriesData?.categories],
    );

    const items = useMemo(
        (): Array<PickerItemData<string>> =>
            documentCategories.map((category) => ({
                value: category.id,
                label: category.label,
                leading: (
                    <DocumentCategoryIcon
                        documentTaxonomy={category.documentTaxonomy}
                    />
                ),
                children: (category.subcategories ?? []).map((subcategory) => ({
                    value: subcategory.id,
                    label: subcategory.label,
                })),
            })),
        [documentCategories],
    );
    const categoriesById = useMemo(
        () =>
            new Map(
                documentCategories
                    .flatMap((category) => [
                        category,
                        ...(category.subcategories ?? []),
                    ])
                    .map((category) => [category.id, category]),
            ),
        [documentCategories],
    );

    const matchesSearch = useCallback(
        (item: PickerItemData<string>, search: string) => {
            const category = categoriesById.get(item.value);
            if (category === undefined) {
                return false;
            }
            if (!category.documentTaxonomy) {
                return category.label
                    .toLowerCase()
                    .includes(search.toLowerCase());
            }
            return matchesDocumentCategorySearch(category, search);
        },
        [categoriesById],
    );

    const selectedIds = useMemo(
        () => filters.documentCategoryIds ?? [],
        [filters.documentCategoryIds],
    );

    const handleValueChange = useCallback(
        (nextIds: string[]) => {
            const nextSelectedIds = applyCategoryToggle({
                selectedIds,
                nextIds,
                categoriesById,
            });
            if (nextSelectedIds === undefined) {
                return;
            }
            onChange({
                documentCategoryIds:
                    nextSelectedIds.length > 0 ? nextSelectedIds : undefined,
            });
        },
        [categoriesById, onChange, selectedIds],
    );

    const reverseSelection = useCallback(() => {
        const selectedSet = new Set(selectedIds);
        const reversedIds = collectItemValues(items).filter(
            (id) => !selectedSet.has(id),
        );
        onChange({
            documentCategoryIds:
                reversedIds.length > 0 ? reversedIds : undefined,
        });
    }, [items, onChange, selectedIds]);

    return (
        <div className={css.filter}>
            <FilterPickerList
                items={items}
                value={selectedIds}
                onValueChange={handleValueChange}
                filter={matchesSearch}
                emptyLabel="No matching categories"
                onDeselect={() => onChange({ documentCategoryIds: undefined })}
                onReverse={reverseSelection}
            />
        </div>
    );
};
